import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
import { theme } from 'antd';

import { MetricsNames } from '@/comp/MapBuilder/MetricsNames';
import { useStores } from '@/hooks/use-stores';
import ConnectivityIcon from '@/comp/MapBuilder/components/ConnectivityIcon/ConnectivityIcon';
import { MaintenanceSwitch } from '@/comp/StationControlCenter/ToggleSwitches/MaintenanceSwitch';
import { PowerOnOffSwitch } from '@/comp/StationControlCenter/ToggleSwitches/PowerOnOffSwitch';
import { getPoweredOnState } from '@/comp/StationControlCenter/SccUtils';
import { AlertsWidget } from '@/comp/MapBuilder/components/AlertsWidget';
import { Ports } from '@/comp/MapBuilder/components/Ports';
import { getWidgetFillColor, isSelectedNodeOnMap } from '@/comp/MapBuilder/utils/utils';
import GetConnectivityIconAndStatus from '@/comp/MapBuilder/nodes/compressor/GetConnectivityIconAndStatus/GetConnectivityIconAndStatus';
import CompressorWidget from '@/comp/common/icons/CompressorWidget';
import CompressorMaintenanceWidget from '@/comp/common/icons/CompressorMaintenanceWidget';
import { CustomThemeToken } from '@/theme';

import { CompressorNodeWidgetContainer } from './CompressorNodeWidget.styles';
import { getBorderInfo, getIsStopped, isDummyCompressor, useMapViewMetrics } from './CompressorNodeWidget.utils';
import { MetricElement } from './MetricElement/MetricElement';
import { usePowerOnOffVisibility } from '../nodeWidget/DetailsDrawer/Footer/StartStopButtons/StartStopButtons.utils';
import PackageCareIndication from '../nodeWidget/DetailsDrawer/PackageCareIndication/PackageCareIndication';

const { useToken } = theme;

export const CompressorNodeWidget = observer(({ engine = null, node, entity, width, height }) => {
  const { t } = useTranslation();
  const { stationStore, mapBuilderStore, pidEditStore } = useStores();
  const { token } = useToken() as unknown as CustomThemeToken;
  const {
    brand,
    modelDisplayName,
    name,
    connectivity,
    activity,
    extendedActivity,
    inMaintenance,
    poweredOn,
    messages,
    controlMode,
    deviceId,
  } = entity;

  const poweredOnState = getPoweredOnState(poweredOn);
  const { poweredOnMetricPresent, powerChangeSupportedAction } = usePowerOnOffVisibility(deviceId);
  const isStopped = getIsStopped(poweredOnMetricPresent, powerChangeSupportedAction, poweredOnState);
  const mapViewMetrics = useMapViewMetrics(entity);
  const currentAsset = stationStore.assetsDevices?.filter(device => device.deviceId === deviceId)[0];
  const renderedOnMap = !!engine;
  const isSelected = isSelectedNodeOnMap(entity.deviceId, mapBuilderStore.selectedDevice?.deviceId, renderedOnMap);
  const borderInfo = getBorderInfo(
    isSelected,
    inMaintenance,
    isStopped,
    isDummyCompressor(entity.deviceId, stationStore),
    token,
  );
  const connectivityIconAndStatus = GetConnectivityIconAndStatus({
    connectivity,
    activity,
    extendedActivity,
    controlMode,
    isSelectedOnMap: isSelected,
    size: renderedOnMap ? '32px' : '32px',
  });
  const backgroundColor = getWidgetFillColor(isSelected, token, renderedOnMap);

  return (
    <CompressorNodeWidgetContainer $mapView={renderedOnMap} $textColor={borderInfo?.textColor}>
      {borderInfo ? (
        <CompressorMaintenanceWidget
          width={width}
          height={height}
          fillColor={borderInfo.fillColor}
          borderColor={borderInfo.borderColor}
        />
      ) : (
        <CompressorWidget width={width} height={height} color={backgroundColor} />
      )}

      <div className="data-layer">
        {borderInfo && <div className="border-header">{t(borderInfo.headerText)}</div>}
        <div className="top-row">
          <div className="control-title-wrapper">
            {!pidEditStore.isInEditMode && (
              <ConnectivityIcon
                messages={messages}
                controlMode={controlMode}
                connectivity={connectivity}
                isCompressor={true}
                helixConnectivityStatus={currentAsset?.helixConnectivityStatusC}
                className={'control-icon'}
                width={renderedOnMap ? '24px' : '32px'}
                height={renderedOnMap ? '24px' : '32px'}
              />
            )}
            {renderedOnMap && (
              <div className="min0 title">
                <div className="name ellipsis">{name}</div>
                <div className="model ellipsis">
                  {modelDisplayName} {brand}
                </div>
              </div>
            )}
          </div>
          <div className={'connectivity-icon-wrapper'}>{connectivityIconAndStatus.icon}</div>
        </div>
        {renderedOnMap && (
          <>
            <div className="middle-row">
              <div className="metrics-list">
                {mapViewMetrics.map(
                  metric =>
                    (metric.name === MetricsNames.Power || metric.value != null) && (
                      <div className="metrics-list-item" key={metric.name}>
                        <MetricElement metric={metric} entity={entity} connectivity={connectivity} />
                      </div>
                    ),
                )}
              </div>
              <AlertsWidget alerts={node?.alerts} />
            </div>
            <PackageCareIndication
              entity={entity}
              mapView
              className="package-care-wrapper"
              backgroundColor={borderInfo?.fillColor}
            />
          </>
        )}
      </div>

      {renderedOnMap ? (
        <Ports node={node} engine={engine} width={width - 10} height={height + 30} />
      ) : (
        <div className="toggles-wrapper">
          {poweredOn != null && (
            <div className="toggle-status-box">
              <PowerOnOffSwitch deviceId={deviceId} forceDisable={true} />
            </div>
          )}
          {!isNil(inMaintenance) && (
            <div className="toggle-status-box">
              <MaintenanceSwitch inMaintenance={inMaintenance} deviceId={deviceId} />
            </div>
          )}
        </div>
      )}
    </CompressorNodeWidgetContainer>
  );
});
